import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import BaseModal from 'components/atoms/base-modal';
import { useCreateConversation } from 'infrastructure/hooks/smsDashboard/useCreateConversation';
import PatientSearch from 'components/molecules/smsDashboard/patientSearch';
import { CONVERSATION_TYPES } from 'store/smsDashboard';

import s from './styles.module.scss';

import type { PatientOptionType } from 'infrastructure/interfaces';
import type { RootDispatch, RootState } from 'store';

export interface INewMessageModalProps {
  isOpen: boolean;
  toggle: () => void;
}

const NewMessageModal: React.FC<INewMessageModalProps> = (props) => {
  const { isOpen, toggle } = props;
  const { t } = useTranslation();

  const [patientData, setPatientData] = useState<PatientOptionType>({
    phoneNumber: '',
    patientGuid: null,
  });
  const resetForm = () => {
    setPatientData({
      phoneNumber: '',
      patientGuid: null,
    });
  };

  const {
    smsDashboard: { changeCurrentConversation },
  } = useDispatch<RootDispatch>();

  const { conversationType } = useSelector(
    (state: RootState) => state.smsDashboard,
  );

  const toggleModal = () => {
    resetForm();
    toggle();
  };

  const { createConversation, createConversationLoading } =
    useCreateConversation({
      onSuccess(data) {
        const res = data.data;
        changeCurrentConversation(res);
        toggleModal();
      },
    });

  const isUnregisteredConversationType =
    conversationType === CONVERSATION_TYPES.UNREGISTERED_RPM ||
    conversationType === CONVERSATION_TYPES.UNREGISTERED_CCM;

  const isNotUnregisteredConversationType =
    conversationType !== CONVERSATION_TYPES.UNREGISTERED_RPM &&
    conversationType !== CONVERSATION_TYPES.UNREGISTERED_CCM;

  const newUnregisteredConversationFrom =
    conversationType === CONVERSATION_TYPES.UNREGISTERED_RPM
      ? CONVERSATION_TYPES.RPM
      : CONVERSATION_TYPES.CCM;

  const onSubmit = async (patient: PatientOptionType) => {
    createConversation({
      patientPhone: patient.phoneNumber,
      patientGuid: isNotUnregisteredConversationType
        ? patient.patientGuid
        : undefined,
      from: isNotUnregisteredConversationType
        ? conversationType
        : newUnregisteredConversationFrom,
    });
  };

  const modalTitle = () => {
    if (isUnregisteredConversationType)
      return t('smsDashboard.new_conversation');
    if (conversationType === CONVERSATION_TYPES.RPM)
      return t('smsDashboard.rpm_patient_list');
    if (conversationType === CONVERSATION_TYPES.CCM)
      return t('smsDashboard.ccm_patient_list');
    return '';
  };

  return (
    <BaseModal
      title={modalTitle()}
      open={isOpen}
      onCancel={toggleModal}
      width="1000px"
    >
      <div className={`${s['new-message-modal']} new-message-modal-padding`}>
        <PatientSearch
          value={patientData.phoneNumber}
          setValue={setPatientData}
          disabled={createConversationLoading}
          onClick={onSubmit}
          conversationType={conversationType}
        />
      </div>
    </BaseModal>
  );
};

export default NewMessageModal;
